import { Suspense, useEffect } from "react";
import { Outlet } from "react-router-dom";
import { SidebarProvider, useSidebar } from "@/components/ui/sidebar";
import { AppSidebar } from "@/components/navigation/AppSidebar";
import { AppNavigation } from "@/components/navigation/AppNavigation";
import { TrialBanner } from "@/components/trial/TrialBanner";
import { TrialExpired } from "@/pages/TrialExpired";
import { useLogout } from "@/hooks/useLogout";
import { useTrialStatus } from "@/hooks/useTrialStatus";
import { useIsMobile } from "@/hooks/use-mobile";
import { SIDEBAR } from "@/lib/uiConstants";
import { ContentSkeleton } from "@/components/layout/ContentSkeleton";
import { ErrorBoundary } from "../ErrorBoundary";
import { preloadMainRoutes } from "@/lib/routePreload";
import { OrdersRealtimeProvider } from "@/providers/OrdersRealtimeProvider";

/**
 * AppShell - Persistent Application Shell
 * 
 * Uses a 2-column grid layout where:
 * - Header spans full width with purple gradient
 * - Header left column aligns with sidebar width (contains toggle + logo)
 * - Header right column contains nav controls
 * - Below header: sidebar + content in same grid structure
 * 
 * CRITICAL: This component must be imported eagerly (not lazy-loaded)
 * to prevent remounts when navigating between app routes.
 */
export function AppShell() {
  return (
    <ErrorBoundary>
    <SidebarProvider>
      <AppShellContent />
    </SidebarProvider>
    </ErrorBoundary>
  );
}

function AppShellContent() {
  const { handleLogout } = useLogout();
  const { status, daysRemaining, isLoading } = useTrialStatus();
  const isMobile = useIsMobile();
  const { open: isExpanded } = useSidebar();

  // Compute sidebar width - single source of truth
  const sidebarWidth = isExpanded ? SIDEBAR.expanded : SIDEBAR.collapsed;

  // Check if trial has ended - but keep shell mounted!
  const isTrialExpired = !isLoading && (
    status === "expired" || 
    (status === "trial" && daysRemaining !== null && daysRemaining <= 0)
  );

  // Preload main routes during browser idle time
  useEffect(() => {
    if ('requestIdleCallback' in window) {
      const id = requestIdleCallback(() => preloadMainRoutes());
      return () => cancelIdleCallback(id);
    } else {
      // Fallback for Safari: preload after 2 seconds
      const timer = setTimeout(preloadMainRoutes, 2000);
      return () => clearTimeout(timer);
    }
  }, []);

  return (
    <OrdersRealtimeProvider>
      <div className="min-h-screen flex flex-col w-full overflow-x-hidden">
        {/* Fixed header - full width purple, 2-column grid layout */}
        <AppNavigation 
          onLogout={handleLogout} 
          sidebarWidth={sidebarWidth}
          className="fixed top-0 left-0 right-0 z-30" 
        />
        
        {/* Main content area - below header */}
        <div 
          className="flex-1 pt-14"
          style={{
            display: 'grid',
            gridTemplateColumns: isMobile ? '1fr' : `${sidebarWidth}px 1fr`,
            transition: `grid-template-columns ${SIDEBAR.animationMs}ms ease`,
          }}
        >
          {/* Sidebar - renders Sheet on mobile, persistent sidebar on desktop */}
          <AppSidebar />
          
          {/* Content area with subtle divider on left */}
          <main className="min-w-0 overflow-x-hidden flex flex-col border-l border-[hsl(var(--sidebar-divider))]">
            {/* Subtle trial banner - ambient presence */}
            <TrialBanner />
            
            {/* Content: either TrialExpired or page content */}
            {isTrialExpired ? (
              <TrialExpired />
            ) : (
              // Suspense boundary here - only content swaps, shell stays mounted
              <Suspense fallback={<ContentSkeleton />}>
                <Outlet />
              </Suspense>
            )}
          </main>
        </div>
      </div>
    </OrdersRealtimeProvider>
  );
}
